import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { organizationsApi } from '@/lib/api';
import { toast, Toaster } from 'react-hot-toast';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

interface OrgInfo {
  organizationId: string;
  name: string;
  slug: string;
  logo?: string;
  settings: {
    cookieDuration: number;
  };
}

interface CampaignInfo {
  name: string;
  type: string;
  commissionValue: number;
}

export default function EmbedAffiliateApplication() {
  const [searchParams] = useSearchParams();
  const orgSlug = searchParams.get('org');

  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    website: '',
    socialMedia: '',
    audienceSize: '',
    promotionPlan: '',
  });

  useEffect(() => {
    const fetchOrg = async () => {
      if (!orgSlug) {
        setError('Organization not specified');
        setIsLoading(false);
        return;
      }

      try {
        const response = await organizationsApi.getBySlug(orgSlug);
        if (response.data.success) {
          setOrgInfo(response.data.data.organization);
          setCampaignInfo(response.data.data.defaultCampaign);
        }
      } catch {
        setError('Organization not found');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrg();
  }, [orgSlug]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      // Register the user as affiliate
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          accountType: 'affiliate',
          acceptedTerms: true,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Registration failed');
      }

      setSubmitted(true);
      toast.success('Application submitted! Check your email to verify your account.');

      // Notify parent window if embedded
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'venndela-affiliate-application',
            status: 'submitted',
            email: formData.email,
            orgSlug,
            verifyUrl: `${window.location.origin}/verify-email?email=${encodeURIComponent(formData.email)}&org=${orgSlug}`,
          },
          '*'
        );
      }
    } catch (err: unknown) {
      const error = err as Error;
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const verifyUrl = `${window.location.origin}/verify-email?email=${encodeURIComponent(formData.email)}&org=${orgSlug}`;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Toaster position="top-center" />
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-600 mb-4">
            Thank you for applying to {orgInfo?.name}&apos;s affiliate program.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Please check your email for a verification code. Once verified, you&apos;ll be able to sign in
            and complete joining the program.
          </p>
          <a
            href={verifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Verify Your Email
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          {orgInfo?.logo ? (
            <img
              src={orgInfo.logo}
              alt={orgInfo.name}
              className="w-16 h-16 rounded-full mx-auto mb-3 object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-white">{orgInfo?.name.charAt(0)}</span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{orgInfo?.name}</h1>
          <p className="text-gray-600 mt-1">Affiliate Program Application</p>
        </div>

        {/* Commission Info */}
        {campaignInfo && (
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm text-gray-700">
              Earn{' '}
              <span className="font-bold text-orange-600">
                {campaignInfo.type === 'percentage'
                  ? `${campaignInfo.commissionValue}% commission`
                  : `$${(campaignInfo.commissionValue / 100).toFixed(2)} per sale`}
              </span>{' '}
              on every referral!
            </p>
          </div>
        )}

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="At least 8 characters"
              minLength={8}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website or Blog</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="https://yourwebsite.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Social Media Profiles</label>
            <input
              type="text"
              name="socialMedia"
              value={formData.socialMedia}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="@username on Instagram, Twitter, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Audience Size</label>
            <select
              name="audienceSize"
              value={formData.audienceSize}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select your reach</option>
              <option value="under-1k">Under 1,000</option>
              <option value="1k-10k">1,000 - 10,000</option>
              <option value="10k-50k">10,000 - 50,000</option>
              <option value="50k-100k">50,000 - 100,000</option>
              <option value="100k+">100,000+</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              How do you plan to promote {orgInfo?.name}?
            </label>
            <textarea
              name="promotionPlan"
              value={formData.promotionPlan}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Describe your promotion strategy..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>

          <p className="text-xs text-gray-500 text-center">
            By applying, you agree to the{' '}
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-4">
          Powered by{' '}
          <a
            href="https://venndela.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-gray-700"
          >
            Venndela
          </a>
        </p>
      </div>
    </div>
  );
}
